/**
 * @file use-modals.ts
 * @description Кастомный хук для управления очередью модальных окон в приложении.
 * Обеспечивает правильный порядок закрытия модалов через Escape, управляет заголовком страницы,
 * автоматически возвращает на главную страницу при закрытии последнего модала.
 * Использует глобальные горячие клавиши и Redux для синхронизации состояния модалов между компонентами.
 * Поддерживает навигацию и автоматическое обновление title страницы на основе типа сущности.
 * @author AI Assistant
 * @created 2025-09-04
 * @updated 2025-09-04
 * @dependencies [react, @consta/uikit/useGlobalKeys, react-redux, @store/slices/modals-queue-slice, react-router-dom, @utils/form-object-helper]
 * @related-files [@store/slices/modals-queue-slice.ts, @utils/form-object-helper.ts]
 */

import { useEffect, useId, useRef } from 'react';
import { useGlobalKeys } from '@consta/uikit/useGlobalKeys';
import { useDispatch, useSelector } from 'react-redux';
import { addItem, removeItem, setItems, getItems } from '@store/slices/modals-queue-slice';
import { useNavigate } from 'react-router-dom';
import { getObjectEntity } from '@utils/form-object-helper';

export const useModalQueue = (closePageHandler: Function, entityType: string = '') => {
    const dispatch = useDispatch();
    const id: string = useId();
    const items: string[] = useSelector(getItems);
    const navigate = useNavigate();
    const itemsRef = useRef(items);

    useEffect(() => {
        dispatch(addItem(id));

        if (!items.length) {
            const entity = getObjectEntity(entityType);

            document.title = `${import.meta.env.VITE_APP_TAB_TITLE}: ${entity ? entity.title : entityType}`;
        }
    }, [id]);

    useEffect(() => {
        itemsRef.current = items;
    }, [items]);

    useGlobalKeys({
        Escape: () => closeHandler()
    });

    const closeHandler = () => {
        if (itemsRef.current.at(-1) !== id) {
            return;
        }

        dispatch(removeItem(id));
        closePageHandler(false);

        if (itemsRef.current.filter((modal: string) => modal !== id).length < 1) {
            dispatch(setItems([]));
            navigate('/');
        }
    };

    return closeHandler;
};
